import { useState } from "react";
import { ArrowUpRight, Puzzle, BookOpen, FolderHeart, BarChart3, LifeBuoy, Sparkles, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const TILES = [
  { title: "Challenge Library", color: "#FFD700", Icon: Puzzle, text: "#1A1A1A" },
  { title: "Teacher Resources", color: "#FF6600", Icon: BookOpen, text: "#FFFFFF" },
  { title: "Student Portfolios", color: "#FF007F", Icon: FolderHeart, text: "#FFFFFF" },
  { title: "Reporting", color: "#0055FF", Icon: BarChart3, text: "#FFFFFF" },
  { title: "Helpdesk", color: "#00C2B8", Icon: LifeBuoy, text: "#1A1A1A" },
  { title: "Early Access", color: "#FFFFFF", Icon: Sparkles, text: "#1A1A1A" },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlatformPreview() {
  const [email, setEmail] = useState("");
  const [org, setOrg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/early-access`, { email, organisation: org || null });
      toast.success("You're on the early access list!");
      setEmail("");
      setOrg("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="platform-section"
      className="bg-[#1A1A1A] text-white relative overflow-hidden border-y-2 border-[#1A1A1A]"
    >
      <div className="absolute inset-0 bg-dots-light opacity-30 pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 bg-[#FFD700] text-[#1A1A1A] border-2 border-[#FFD700] rounded-full px-3 py-1 font-mono text-[11px] uppercase tracking-widest font-bold">
              <span className="h-2 w-2 rounded-full bg-[#FF007F] animate-spark" />
              Coming Soon
            </div>
            <h2 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
              The future{" "}
              <span className="text-[#00C2B8]">platform</span> for future-ready
              learning.
            </h2>
            <p className="mt-5 text-base md:text-lg text-white/80 font-medium leading-relaxed max-w-md">
              The TinkerShift Platform is being designed as a practical SaaS
              hub for schools and libraries — a single home for challenges,
              resources, portfolios and progress.
            </p>

            <form
              onSubmit={onSubmit}
              data-testid="early-access-form"
              className="mt-8 bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-3xl p-5 nb-shadow"
            >
              <p className="font-mono text-[11px] uppercase tracking-widest text-[#FF007F] font-bold">
                Join the Early Access List
              </p>
              <div className="mt-3 grid grid-cols-1 gap-3">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@school.edu"
                  data-testid="early-access-email-input"
                  className="border-2 border-[#1A1A1A] rounded-xl px-4 py-3 font-semibold focus:outline-none focus:ring-4 focus:ring-[#FFD700]"
                />
                <input
                  type="text"
                  value={org}
                  onChange={(e) => setOrg(e.target.value)}
                  placeholder="Organisation (optional)"
                  data-testid="early-access-org-input"
                  className="border-2 border-[#1A1A1A] rounded-xl px-4 py-3 font-semibold focus:outline-none focus:ring-4 focus:ring-[#FFD700]"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="early-access-submit-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white border-2 border-[#1A1A1A] rounded-full px-5 py-3 font-bold uppercase tracking-wide nb-shadow-sm nb-press disabled:opacity-70"
                >
                  {submitting ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <ArrowUpRight size={16} strokeWidth={2.5} />
                  )}
                  {submitting ? "Joining..." : "Join the list"}
                </button>
              </div>
            </form>
          </div>

          {/* Mock dashboard */}
          <div className="lg:col-span-7">
            <div className="rounded-3xl border-2 border-white/15 bg-[#0F0F10] p-4 md:p-6 nb-shadow-lg overflow-hidden">
              <div className="flex items-center gap-2 pb-3 border-b border-white/10">
                <span className="h-3 w-3 rounded-full bg-[#FF007F]" />
                <span className="h-3 w-3 rounded-full bg-[#FFD700]" />
                <span className="h-3 w-3 rounded-full bg-[#00C2B8]" />
                <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
                  tinkershift.app / dashboard
                </span>
              </div>
              <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-4">
                {TILES.map(({ title, color, Icon, text }) => (
                  <div
                    key={title}
                    data-testid={`platform-tile-${title.toLowerCase().replace(/\s+/g, "-")}`}
                    style={{ backgroundColor: color, color: text }}
                    className="rounded-2xl border-2 border-[#1A1A1A] p-4 md:p-5 nb-shadow nb-lift"
                  >
                    <div className="flex items-center justify-between">
                      <Icon size={22} strokeWidth={2.5} />
                      <span className="font-mono text-[10px] uppercase tracking-widest opacity-70">
                        Module
                      </span>
                    </div>
                    <p className="mt-4 font-display font-black text-lg md:text-xl leading-tight">
                      {title}
                    </p>
                    <div className="mt-3 flex gap-1">
                      <span className="h-1.5 w-6 rounded-full bg-current opacity-90" />
                      <span className="h-1.5 w-4 rounded-full bg-current opacity-60" />
                      <span className="h-1.5 w-2 rounded-full bg-current opacity-30" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2 rounded-2xl border-2 border-white/15 p-4 bg-white/5">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/60">
                    Class progress · Year 6 · STEM
                  </p>
                  <div className="mt-3 flex items-end gap-1.5 h-20">
                    {[40, 60, 45, 70, 55, 80, 65, 90, 75, 95, 85, 100].map(
                      (v, i) => (
                        <span
                          key={i}
                          style={{ height: `${v}%` }}
                          className={`w-3 rounded-t-md ${
                            i % 4 === 0
                              ? "bg-[#FFD700]"
                              : i % 4 === 1
                              ? "bg-[#FF007F]"
                              : i % 4 === 2
                              ? "bg-[#00C2B8]"
                              : "bg-[#0055FF]"
                          }`}
                        />
                      ),
                    )}
                  </div>
                </div>
                <div className="rounded-2xl border-2 border-white/15 p-4 bg-white/5">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/60">
                    Active challenges
                  </p>
                  <p className="mt-3 font-display font-black text-4xl">128</p>
                  <p className="font-mono text-xs text-[#00C2B8] mt-1">
                    +12 this week
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
